import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import { useQrScanner } from '../../hooks/useQrScanner';
import {
  Search, History, MapPin, FileText, Truck, Camera, X,
  RefreshCw, DollarSign, Download
} from 'lucide-react';

// Extracted sub-components
import { TraceabilityKPIs } from '../../components/traceability/TraceabilityKPIs';
import { StockEvolutionChart } from '../../components/traceability/StockEvolutionChart';
import { MovementsTab } from '../../components/traceability/MovementsTab';
import { LocationsTab } from '../../components/traceability/LocationsTab';
import { DocumentsTab } from '../../components/traceability/DocumentsTab';
import { PricesTab } from '../../components/traceability/PricesTab';
import { SuppliersTab } from '../../components/traceability/SuppliersTab';
import { DocumentDetailModal } from '../../components/traceability/DocumentDetailModal';

const formatNumber = (num) => {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('ro-RO').format(num);
};

const ArticleTraceabilityPage = () => {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();
  const scanContainerId = 'traceability-qr-scanner';

  const [summary, setSummary] = useState(null);
  const [stockEvolution, setStockEvolution] = useState(null);
  const [movements, setMovements] = useState({ items: [], total: 0, page: 1 });
  const [locations, setLocations] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [articlesLoading, setArticlesLoading] = useState(true);

  const [movementFilter, setMovementFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [chartPeriod, setChartPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('timeline');

  const [detailModal, setDetailModal] = useState({ open: false, type: null, data: null, loading: false });

  useEffect(() => {
    (async () => {
      setArticlesLoading(true);
      try {
        const res = await api.get('/articles');
        setArticles(res.data);
      } catch {
        toast.error('Eroare la incarcarea articolelor');
      } finally {
        setArticlesLoading(false);
      }
    })();
  }, []);

  const handleArticleSelect = async (article) => {
    if (!article) return;
    setSelectedArticle(article);
    setLoading(true);
    try {
      const [summaryRes, evolutionRes, movementsRes, locationsRes, docsRes, pricesRes] = await Promise.all([
        api.get(`/traceability/article/${article.id}/summary`),
        api.get(`/traceability/article/${article.id}/stock-evolution?days=${chartPeriod}`),
        api.get(`/traceability/article/${article.id}/movements?page=1&page_size=50`),
        api.get(`/traceability/article/${article.id}/locations`),
        api.get(`/traceability/article/${article.id}/documents`),
        api.get(`/traceability/article/${article.id}/price-history`)
      ]);
      setSummary(summaryRes.data);
      setStockEvolution(evolutionRes.data);
      setMovements(movementsRes.data);
      setLocations(locationsRes.data);
      setDocuments(docsRes.data);
      setPriceHistory(pricesRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async (page = 1) => {
    if (!selectedArticle) return;
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (movementFilter !== 'all') params.append('movement_type', movementFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/traceability/article/${selectedArticle.id}/movements?${params}`);
      setMovements(res.data);
    } catch {
      toast.error('Eroare la incarcarea miscarilor');
    }
  };

  const handleChartPeriodChange = async (v) => {
    setChartPeriod(v);
    if (selectedArticle) {
      try {
        const res = await api.get(`/traceability/article/${selectedArticle.id}/stock-evolution?days=${v}`);
        setStockEvolution(res.data);
      } catch (e) { console.error(e); }
    }
  };

  const handleStartScan = async () => {
    setScanning(true);
    try {
      await startQr(scanContainerId, (text) => handleQrResult(text));
    } catch {
      toast.error('Nu s-a putut porni camera');
      setScanning(false);
    }
  };

  const handleStopScan = async () => {
    await stopQr();
    setScanning(false);
  };

  const handleQrResult = useCallback(async (scannedText) => {
    await stopQr();
    setScanning(false);
    let articleCode = scannedText;
    if (scannedText.startsWith('ART:')) articleCode = scannedText.substring(4);
    const matched = articles.find(a => a.code === articleCode || a.id === articleCode || (a.code && scannedText.includes(a.code)));
    if (matched) {
      handleArticleSelect(matched);
      toast.success(`Articol gasit: ${matched.name}`);
    } else {
      toast.error(`Articol negasit: ${articleCode}`);
    }
  }, [articles, stopQr]);

  const openDetailModal = async (type, id) => {
    if (!id) { toast.error('Document ID nu este disponibil'); return; }
    setDetailModal({ open: true, type, data: null, loading: true });
    try {
      let data = null;
      if (type === 'receipt' || type === 'quick_receipt') {
        data = (await api.get(`/wms/receipts/${id}`)).data;
      } else if (type === 'ticket') {
        data = (await api.get(`/tickets/${id}`)).data;
      } else if (type === 'adjustment') {
        const res = await api.get(`/wms/stock/adjustments/history`);
        data = res.data?.find(a => a.id === id) || { id, note: 'Detalii indisponibile' };
      } else if (type === 'transfer') {
        data = { id, type: 'transfer', note: 'Transfer stoc' };
      } else {
        data = { id, type, note: 'Document' };
      }
      setDetailModal({ open: true, type, data, loading: false });
    } catch {
      setDetailModal({ open: true, type, data: { error: true, message: 'Nu s-au putut incarca detaliile' }, loading: false });
    }
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <History className="w-7 h-7 text-indigo-600" />
            Trasabilitate Articol
          </h1>
          <p className="text-slate-500 text-sm mt-1">Fisa completa si istoricul miscarilor pentru articole</p>
        </div>
        {selectedArticle && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleArticleSelect(selectedArticle)} className="gap-1.5">
              <RefreshCw className="w-4 h-4" /> Actualizeaza
            </Button>
            <Button variant="outline" size="sm" onClick={() => toast.info('Export PDF in dezvoltare...')} className="gap-1.5">
              <Download className="w-4 h-4" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => toast.info('Export Excel in dezvoltare...')} className="gap-1.5">
              <FileText className="w-4 h-4" /> Excel
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <Card className="border-2 border-indigo-100">
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-2 block">
                Cauta Articol
                {articlesLoading && <span className="text-slate-400 ml-2">(se incarca...)</span>}
                {!articlesLoading && articles.length > 0 && <span className="text-slate-400 ml-2 text-xs">({articles.length} articole)</span>}
              </Label>
              <ArticleAutocomplete
                articles={articles}
                onSelect={handleArticleSelect}
                placeholder={articlesLoading ? "Se incarca articolele..." : "Cauta dupa nume, cod, cod furnizor..."}
              />
            </div>
            {!scanning ? (
              <Button variant="outline" onClick={handleStartScan} className="h-10 gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="scan-qr-btn">
                <Camera className="w-4 h-4" /> Scan QR
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleStopScan} className="h-10 gap-2">
                <X className="w-4 h-4" /> Opreste
              </Button>
            )}
          </div>
          {scanning && (
            <div className="mt-4 relative rounded-xl overflow-hidden bg-black max-w-md mx-auto">
              <div id={scanContainerId} className="w-full" style={{ minHeight: 250 }} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
          <Skeleton className="h-64 w-full" />
        </div>
      ) : selectedArticle && summary ? (
        <>
          {/* Article Info Card */}
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{summary.article?.name}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge className="bg-white/20 text-white hover:bg-white/30 font-mono">{summary.article?.code}</Badge>
                    {summary.article?.category_name && <Badge className="bg-white/20 text-white hover:bg-white/30">{summary.article.category_name}</Badge>}
                    {summary.article?.unit && <span className="text-white/80 text-sm">U.M.: {summary.article.unit}</span>}
                  </div>
                  {summary.article?.default_bin_location && (
                    <p className="text-white/80 text-sm mt-2 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> BIN preferat: {summary.article.default_bin_location}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">Stoc Total Curent</p>
                  <p className="text-4xl font-bold">{formatNumber(summary.kpis?.total_stock)}</p>
                  <p className="text-white/80 text-sm">{summary.article?.unit || 'buc'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <TraceabilityKPIs kpis={summary.kpis} />
          <StockEvolutionChart stockEvolution={stockEvolution} chartPeriod={chartPeriod} onPeriodChange={handleChartPeriodChange} />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
              <TabsTrigger value="timeline" className="gap-1.5"><History className="w-4 h-4" /> Miscari</TabsTrigger>
              <TabsTrigger value="locations" className="gap-1.5"><MapPin className="w-4 h-4" /> Locatii</TabsTrigger>
              <TabsTrigger value="documents" className="gap-1.5"><FileText className="w-4 h-4" /> Documente</TabsTrigger>
              <TabsTrigger value="prices" className="gap-1.5"><DollarSign className="w-4 h-4" /> Preturi</TabsTrigger>
              <TabsTrigger value="suppliers" className="gap-1.5"><Truck className="w-4 h-4" /> Furnizori</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <MovementsTab
                movements={movements} movementFilter={movementFilter}
                dateFrom={dateFrom} dateTo={dateTo}
                onFilterChange={setMovementFilter} onDateFromChange={setDateFrom}
                onDateToChange={setDateTo} onFetchMovements={fetchMovements}
                onViewDetail={openDetailModal}
              />
            </TabsContent>
            <TabsContent value="locations"><LocationsTab locations={locations} /></TabsContent>
            <TabsContent value="documents"><DocumentsTab documents={documents} onViewDetail={openDetailModal} /></TabsContent>
            <TabsContent value="prices"><PricesTab priceHistory={priceHistory} /></TabsContent>
            <TabsContent value="suppliers"><SuppliersTab suppliers={priceHistory?.suppliers} /></TabsContent>
          </Tabs>
        </>
      ) : (
        <Card className="py-16">
          <CardContent className="text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">Selecteaza un articol</h3>
            <p className="text-slate-400 mt-1">Cauta sau scaneaza codul QR pentru a vedea fisa completa</p>
          </CardContent>
        </Card>
      )}

      <DocumentDetailModal detailModal={detailModal} onClose={() => setDetailModal({ open: false, type: null, data: null, loading: false })} />
    </div>
  );
};

export default ArticleTraceabilityPage;
